import React, { useState, useEffect } from "react";
import { useLang } from "@/lib/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck, Globe } from "lucide-react";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Header() {
  const { t, lang, toggle, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollTo(id), 250);
    } else {
      scrollTo(id);
    }
  };

  const links = [
    { id: "free-tip", label: t("freeTip.title") },
    { id: "performance", label: t("nav.performance") },
    { id: "about", label: t("nav.about") },
    { id: "archive", label: t("nav.archive") },
    { id: "pricing", label: t("nav.pricing") },
    { id: "contact", label: t("nav.contact") },
  ];

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 transition-shadow duration-300"
      style={{
        background: scrolled ? "rgba(56,4,12,0.9)" : "rgba(56,4,12,0.72)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderBottom: "1px solid var(--border-gold)",
        boxShadow: scrolled ? "0 8px 30px rgba(0,0,0,0.4)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-center justify-between h-16">
          <button data-testid="logo-btn" onClick={() => go("top")} className="flex items-center gap-2.5 group">
            <div className="relative w-9 h-9 rounded-md overflow-hidden flex flex-col shadow-sm ring-1 ring-[var(--border-gold)]">
              <div className="flex-1 bg-white" />
              <div className="flex-1" style={{ background: "var(--austria-red)" }} />
              <div className="flex-1 bg-white" />
            </div>
            <div className="text-left leading-none">
              <div className="font-serif-display font-bold tracking-tight text-xl text-white">
                Austria<span className="gold-gradient-text"> Tips</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.28em] text-white/55 font-heading mt-0.5">
                Vienna · Est. 2021
              </div>
            </div>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => go(l.id)}
                className="font-heading text-[13px] font-semibold text-white/75 hover:text-white transition-colors relative after:absolute after:-bottom-1.5 after:left-0 after:h-0.5 after:w-0 after:bg-[var(--trust-gold)] hover:after:w-full after:transition-all"
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div
              data-testid="lang-toggle"
              aria-label="Translate website language"
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border border-[var(--border-gold)] bg-white/5"
            >
              <Globe size={15} className="text-[var(--trust-gold)]" />
              <span className="text-[9px] uppercase tracking-wide font-extrabold font-heading text-white/80">Translate</span>
              <span className="text-xs font-bold font-heading flex items-center gap-1">
                <button data-testid="lang-en" onClick={() => setLang("en")} className={`transition-colors ${lang === "en" ? "text-[var(--trust-gold)]" : "text-white/55 hover:text-white"}`}>EN</button>
                <span className="text-white/30">/</span>
                <button data-testid="lang-de" onClick={() => setLang("de")} className={`transition-colors ${lang === "de" ? "text-[var(--trust-gold)]" : "text-white/55 hover:text-white"}`}>DE</button>
              </span>
            </div>
            <button
              data-testid="header-cta"
              onClick={() => go("pricing")}
              className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-bold font-heading text-[#38040C] px-4 py-2 rounded-full transition-transform hover:scale-105 active:scale-95 shadow-[0_6px_20px_rgba(255,215,0,0.3)]"
              style={{ background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" }}
            >
              <ShieldCheck size={15} /> {t("nav.cta")}
            </button>
            <button data-testid="mobile-menu-btn" onClick={() => setOpen(!open)} className="lg:hidden p-2 text-white">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden px-5 py-4 space-y-1 border-t border-[var(--border-gold)]" style={{ background: "var(--at-darkest)" }}>
          {links.map((l) => (
            <button key={l.id} data-testid={`mobile-nav-${l.id}`} onClick={() => go(l.id)} className="block w-full text-left py-3 font-heading font-semibold text-white/85 border-b border-white/10">
              {l.label}
            </button>
          ))}
          <div className="flex items-center gap-3 pt-3">
            <button data-testid="mobile-lang-toggle" onClick={toggle} className="flex-1 py-2.5 rounded-full border border-[var(--border-gold)] text-white text-sm font-bold">
              {lang === "de" ? "English" : "Deutsch"}
            </button>
            <button data-testid="mobile-cta" onClick={() => go("pricing")} className="flex-1 py-2.5 rounded-full text-[#38040C] text-sm font-bold" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700,#D4AF37)" }}>
              {t("nav.cta")}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
